import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoAPI, generateAPI, folderAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BatchGenerateModal } from '@/components/BatchGenerateModal';
import {
  Loader2, Video, Sparkles, ArrowRight, CheckCircle2, Clock, AlertCircle,
  Square, CheckSquare, FolderPlus, Folder, FolderOpen, MoreVertical, Pencil,
  Trash2, Plus, Library, MoveRight, ChevronRight, FolderInput, PanelLeftClose, PanelLeftOpen,
  Download, LayoutGrid, List
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const statusConfig = {
  processing: { label: 'Processing', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  extracting_audio: { label: 'Extracting', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  transcribing: { label: 'Transcribing', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  ready: { label: 'Ready', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  queued: { label: 'Queued', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
};

const FOLDER_COLORS = [
  { id: 'indigo', bg: 'bg-indigo-500/15', text: 'text-indigo-400', dot: 'bg-indigo-400' },
  { id: 'purple', bg: 'bg-purple-500/15', text: 'text-purple-400', dot: 'bg-purple-400' },
  { id: 'emerald', bg: 'bg-emerald-500/15', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  { id: 'amber', bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-400' },
  { id: 'rose', bg: 'bg-rose-500/15', text: 'text-rose-400', dot: 'bg-rose-400' },
  { id: 'cyan', bg: 'bg-cyan-500/15', text: 'text-cyan-400', dot: 'bg-cyan-400' },
];

const getColor = (colorId) => FOLDER_COLORS.find(c => c.id === colorId) || FOLDER_COLORS[0];

export default function LibraryPage() {
  const [videos, setVideos] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [folderData, setFolderData] = useState({ folders: [], uncategorized_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('all'); // 'all' | 'uncategorized' | folder_id
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('indigo');
  const [savingFolder, setSavingFolder] = useState(false);
  const [foldersPanelCollapsed, setFoldersPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('videos');
  const [genView, setGenView] = useState('grid');
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = useCallback(async () => {
    try {
      const [videosRes, gensRes, foldersRes] = await Promise.all([
        videoAPI.list(),
        generateAPI.historyEnriched(),
        folderAPI.list(),
      ]);
      setVideos(videosRes.data);
      setGenerations(gensRes.data);
      setFolderData(foldersRes.data);
    } catch (err) {
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  }, []);

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  // Filter videos by folder
  const filteredVideos = videos.filter(v => {
    if (activeFolder === 'all') return true;
    if (activeFolder === 'uncategorized') return !v.folder_id;
    return v.folder_id === activeFolder;
  });

  const readyVideos = filteredVideos.filter(v => v.status === 'ready');

  // Filter generations to match filtered videos
  const filteredVideoIds = new Set(filteredVideos.map(v => v.id));
  const filteredGenerations = activeFolder === 'all'
    ? generations
    : generations.filter(g => filteredVideoIds.has(g.video_id));

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredVideos.length && filteredVideos.length > 0) setSelectedIds([]);
    else setSelectedIds(filteredVideos.map(v => v.id));
  };

  // Folder CRUD
  const openCreateFolder = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderColor('indigo');
    setShowFolderModal(true);
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || 'indigo');
    setShowFolderModal(true);
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) return;
    setSavingFolder(true);
    try {
      if (editingFolder) {
        await folderAPI.update(editingFolder.id, { name: folderName.trim(), color: folderColor });
        toast.success('Folder updated');
      } else {
        await folderAPI.create(folderName.trim(), folderColor);
        toast.success('Folder created');
      }
      setShowFolderModal(false);
      loadData();
    } catch (err) {
      toast.error('Failed to save folder');
    } finally {
      setSavingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await folderAPI.delete(folderId);
      if (activeFolder === folderId) setActiveFolder('all');
      toast.success('Folder deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveVideo = async (videoId, folderId) => {
    try {
      await folderAPI.assignVideo(videoId, folderId);
      toast.success(folderId ? 'Moved to folder' : 'Moved to Uncategorized');
      loadData();
    } catch (err) {
      toast.error('Failed to move video');
    }
  };

  const handleBatchComplete = () => {
    setSelectedIds([]);
    navigate('/pipeline');
  };

  const handleExportSelected = async () => {
    if (selectedIds.length === 0) return;
    setExporting(true);
    try {
      const blob = await videoAPI.exportSelected(selectedIds);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clippost_export_${selectedIds.length}_clips.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };


  const handleBatchMoveToFolder = async (folderId) => {
    try {
      await folderAPI.batchAssign(selectedIds, folderId);
      const folderName = folderId
        ? folderData.folders.find(f => f.id === folderId)?.name || 'folder'
        : 'Uncategorized';
      toast.success(`${selectedIds.length} ${selectedIds.length === 1 ? 'video' : 'videos'} moved to ${folderName}`);
      setSelectedIds([]);
      loadData();
    } catch (err) {
      toast.error('Failed to move videos');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeFolderObj = folderData.folders.find(f => f.id === activeFolder);
  const selectedReadyIds = selectedIds.filter(id => videos.find(v => v.id === id)?.status === 'ready');

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="library-page">
      {/* ─── Folder Sidebar ─── */}
      <div
        className={cn(
          'shrink-0 border-r border-border/30 bg-zinc-950/40 flex flex-col transition-all duration-300 ease-in-out overflow-hidden',
          foldersPanelCollapsed ? 'w-12' : 'w-60'
        )}
        data-testid="folder-sidebar"
      >
        {/* Sidebar Header */}
        <div className={cn('border-b border-border/20 flex items-center', foldersPanelCollapsed ? 'px-2 py-5 justify-center' : 'px-4 py-5 justify-between')}>
          {!foldersPanelCollapsed && (
            <h3 className="font-heading text-sm font-semibold text-muted-foreground uppercase tracking-wider">Folders</h3>
          )}
          <div className="flex items-center gap-0.5">
            {!foldersPanelCollapsed && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={openCreateFolder} data-testid="create-folder-button">
                <FolderPlus className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setFoldersPanelCollapsed(!foldersPanelCollapsed)}
              data-testid="toggle-folders-panel"
            >
              {foldersPanelCollapsed
                ? <PanelLeftOpen className="w-3.5 h-3.5" />
                : <PanelLeftClose className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {/* Collapsed state: icon-only nav */}
        {foldersPanelCollapsed ? (
          <div className="flex flex-col items-center py-3 gap-1" data-testid="collapsed-folder-nav">
            <button
              onClick={() => { setActiveFolder('all'); setSelectedIds([]); setFoldersPanelCollapsed(false); }}
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                activeFolder === 'all' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              )}
              title="All Videos"
              data-testid="collapsed-folder-all"
            >
              <Library className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => { setActiveFolder('uncategorized'); setSelectedIds([]); setFoldersPanelCollapsed(false); }}
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                activeFolder === 'uncategorized' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              )}
              title="Uncategorized"
              data-testid="collapsed-folder-uncategorized"
            >
              <Folder className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {folderData.folders.map((folder) => {
              const clr = getColor(folder.color);
              return (
                <button
                  key={folder.id}
                  onClick={() => { setActiveFolder(folder.id); setSelectedIds([]); setFoldersPanelCollapsed(false); }}
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                    activeFolder === folder.id ? `${clr.bg} ${clr.text}` : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
                  )}
                  title={folder.name}
                  data-testid={`collapsed-folder-${folder.id}`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${clr.dot}`} />
                </button>
              );
            })}
          </div>
        ) : (
          /* Expanded state: full folder list */
          <ScrollArea className="flex-1">
            <div className="px-2 py-3 space-y-0.5">
            {/* All */}
            <button
              onClick={() => { setActiveFolder('all'); setSelectedIds([]); }}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-all duration-200',
                activeFolder === 'all'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              )}
              data-testid="folder-all"
            >
              <div className="flex items-center gap-2">
                <Library className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-medium">All Videos</span>
              </div>
              <span className="text-[10px] tabular-nums">{folderData.total_count}</span>
            </button>

            {/* Uncategorized */}
            <button
              onClick={() => { setActiveFolder('uncategorized'); setSelectedIds([]); }}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-all duration-200',
                activeFolder === 'uncategorized'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              )}
              data-testid="folder-uncategorized"
            >
              <div className="flex items-center gap-2">
                <Folder className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-medium">Uncategorized</span>
              </div>
              <span className="text-[10px] tabular-nums">{folderData.uncategorized_count}</span>
            </button>

            {/* Divider */}
            {folderData.folders.length > 0 && <div className="h-px bg-border/20 my-2" />}

            {/* User Folders */}
            {folderData.folders.map((folder) => {
              const clr = getColor(folder.color);
              return (
                <div key={folder.id} className="group relative">
                  <button
                    onClick={() => { setActiveFolder(folder.id); setSelectedIds([]); }}
                    className={cn(
                      'flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-all duration-200',
                      activeFolder === folder.id
                        ? `${clr.bg} ${clr.text}`
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
                    )}
                    data-testid={`folder-${folder.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-2 rounded-full ${clr.dot} shrink-0`} />
                      <span className="font-medium truncate">{folder.name}</span>
                    </div>
                    <span className="text-[10px] tabular-nums">{folder.video_count || 0}</span>
                  </button>

                  {/* Folder actions */}
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`folder-menu-${folder.id}`}>
                          <MoreVertical className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-36 bg-zinc-900 border-border/50">
                        <DropdownMenuItem onClick={() => openEditFolder(folder)} className="cursor-pointer">
                          <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeleteFolder(folder.id)} className="cursor-pointer text-destructive focus:text-destructive">
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}

            {/* Create folder hint */}
            <button
              onClick={openCreateFolder}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground/50 hover:text-muted-foreground hover:bg-secondary/30 transition-all duration-200"
              data-testid="create-folder-inline"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Folder</span>
            </button>
          </div>
        </ScrollArea>
        )}
      </div>

      {/* ─── Main Content ─── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">
                {activeFolder === 'all' ? 'All Videos' :
                 activeFolder === 'uncategorized' ? 'Uncategorized' :
                 activeFolderObj?.name || 'Library'}
              </h1>
              <p className="text-muted-foreground mt-1 text-sm">
                {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
                {filteredGenerations.length > 0 && ` · ${filteredGenerations.length} generations`}
              </p>
            </div>
            {activeTab === 'videos' && selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Move to Folder */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="secondary"
                      className="rounded-full transition-all duration-300 hover:scale-[1.02]"
                      data-testid="batch-move-folder-button"
                    >
                      <FolderInput className="w-4 h-4 mr-2" /> Move to Folder ({selectedIds.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 bg-zinc-900 border-border/50" data-testid="batch-move-dropdown">
                    <DropdownMenuItem
                      onClick={() => handleBatchMoveToFolder(null)}
                      className="cursor-pointer"
                    >
                      <Folder className="w-3.5 h-3.5 mr-2" /> Uncategorized
                    </DropdownMenuItem>
                    {folderData.folders.length > 0 && <DropdownMenuSeparator />}
                    {folderData.folders.map((f) => {
                      const clr = getColor(f.color);
                      return (
                        <DropdownMenuItem
                          key={f.id}
                          onClick={() => handleBatchMoveToFolder(f.id)}
                          className="cursor-pointer"
                          data-testid={`batch-move-to-${f.id}`}
                        >
                          <div className={`w-2.5 h-2.5 rounded-full ${clr.dot} mr-2`} /> {f.name}
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={openCreateFolder} className="cursor-pointer text-primary">
                      <FolderPlus className="w-3.5 h-3.5 mr-2" /> New Folder
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Generate Selected */}
                <Button
                  onClick={() => setShowBatchModal(true)}
                  className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-[1.02]"
                  data-testid="generate-selected-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Generate ({selectedIds.length})
                </Button>

                {/* Export Selected */}
                <Button
                  onClick={handleExportSelected}
                  disabled={exporting}
                  variant="secondary"
                  className="rounded-full transition-all duration-300 hover:scale-[1.02]"
                  data-testid="export-selected-button"
                >
                  {exporting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
                    : <><Download className="w-4 h-4 mr-2" /> Download ({selectedIds.length})</>
                  }
                </Button>
              </div>
            )}
          </div>

          {/* Tabs: Videos | Generated */}
          <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="library-tabs">
            <div className="flex items-center justify-between">
              <TabsList className="bg-secondary/40 h-9" data-testid="library-tabs-list">
                <TabsTrigger value="videos" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary text-xs gap-1.5" data-testid="tab-videos">
                  <Video className="w-3.5 h-3.5" /> Videos
                  <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 ml-0.5">{filteredVideos.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="generated" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary text-xs gap-1.5" data-testid="tab-generated">
                  <Sparkles className="w-3.5 h-3.5" /> Generated
                  <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 ml-0.5">{filteredGenerations.length}</Badge>
                </TabsTrigger>
              </TabsList>

              {/* Tab-specific actions */}
              {activeTab === 'videos' && (
                <div className="flex items-center gap-2">
                  {filteredVideos.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={toggleSelectAll} className="text-xs text-muted-foreground hover:text-foreground" data-testid="select-all-button">
                      {selectedIds.length === filteredVideos.length && filteredVideos.length > 0 ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate('/upload')} className="text-xs text-primary" data-testid="upload-from-library">
                    <Plus className="w-3 h-3 mr-1" /> Upload
                  </Button>
                </div>
              )}
              {activeTab === 'generated' && filteredGenerations.length > 0 && (
                <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-0.5" data-testid="gen-view-toggle">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("h-7 w-7 p-0", genView === 'grid' && "bg-primary/20 text-primary")}
                    onClick={() => setGenView('grid')}
                    data-testid="gen-view-grid"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("h-7 w-7 p-0", genView === 'list' && "bg-primary/20 text-primary")}
                    onClick={() => setGenView('list')}
                    data-testid="gen-view-list"
                  >
                    <List className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>

            {/* ─── Videos Tab ─── */}
            <TabsContent value="videos" className="mt-4" data-testid="videos-tab-content">
              {filteredVideos.length === 0 ? (
                <Card className="bg-zinc-900/60 border-white/5">
                  <CardContent className="p-0">
                    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-videos">
                      <Video className="w-10 h-10 text-muted-foreground/30 mb-3" strokeWidth={1.5} />
                      <p className="text-sm text-muted-foreground">
                        {activeFolder === 'all' ? 'No videos uploaded yet' : 'No videos in this folder'}
                      </p>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/upload')} className="mt-2 text-primary">
                        Upload a clip
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filteredVideos.map((video) => {
                    const sc = statusConfig[video.status] || statusConfig.processing;
                    const isReady = video.status === 'ready';
                    const isSelected = selectedIds.includes(video.id);
                    const videoFolder = folderData.folders.find(f => f.id === video.folder_id);

                    return (
                      <div
                        key={video.id}
                        className={cn(
                          'flex items-center justify-between p-3 rounded-xl border transition-all duration-200',
                          isSelected
                            ? 'bg-primary/5 border-primary/20'
                            : 'bg-secondary/20 border-white/5 hover:border-white/10'
                        )}
                        data-testid={`library-video-${video.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <button onClick={() => toggleSelect(video.id)} className="shrink-0" data-testid={`select-video-${video.id}`}>
                            {isSelected
                              ? <CheckSquare className="w-4.5 h-4.5 text-primary" />
                              : <Square className="w-4.5 h-4.5 text-muted-foreground/30 hover:text-muted-foreground" />}
                          </button>
                          <div className="w-9 h-9 rounded-lg bg-secondary/60 flex items-center justify-center shrink-0">
                            <Video className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1 cursor-pointer" onClick={() => isReady ? navigate(`/video/${video.id}`) : null}>
                            <p className="text-sm font-medium truncate">{video.filename}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-[10px] text-muted-foreground">{formatSize(video.file_size)} &middot; {formatDate(video.created_at)}</p>
                              {videoFolder && (
                                <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 border-0 ${getColor(videoFolder.color).bg} ${getColor(videoFolder.color).text}`}>
                                  {videoFolder.name}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className={`text-[10px] ${sc.color}`}>{sc.label}</Badge>

                          {/* Move to folder dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`video-menu-${video.id}`}>
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-border/50" data-testid={`video-dropdown-${video.id}`}>
                              {isReady && (
                                <>
                                  <DropdownMenuItem onClick={() => navigate(`/video/${video.id}`)} className="cursor-pointer">
                                    <ArrowRight className="w-3.5 h-3.5 mr-2" /> Open
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <div className="px-2 py-1.5 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Move to</div>
                              <DropdownMenuItem
                                onClick={() => handleMoveVideo(video.id, null)}
                                className="cursor-pointer"
                                disabled={!video.folder_id}
                              >
                                <Folder className="w-3.5 h-3.5 mr-2" /> Uncategorized
                              </DropdownMenuItem>
                              {folderData.folders.map((f) => {
                                const clr = getColor(f.color);
                                return (
                                  <DropdownMenuItem
                                    key={f.id}
                                    onClick={() => handleMoveVideo(video.id, f.id)}
                                    className="cursor-pointer"
                                    disabled={video.folder_id === f.id}
                                  >
                                    <div className={`w-2.5 h-2.5 rounded-full ${clr.dot} mr-2`} /> {f.name}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* ─── Generated Tab ─── */}
            <TabsContent value="generated" className="mt-4" data-testid="generated-tab-content">
              {filteredGenerations.length === 0 ? (
                <Card className="bg-zinc-900/60 border-white/5">
                  <CardContent className="p-0">
                    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-generations">
                      <Sparkles className="w-10 h-10 text-muted-foreground/30 mb-3" strokeWidth={1.5} />
                      <p className="text-sm text-muted-foreground">No generated content yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">Select a video and preset to start generating</p>
                    </div>
                  </CardContent>
                </Card>
              ) : genView === 'grid' ? (
                /* Grid / Card view */
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredGenerations.map((gen) => (
                    <div
                      key={gen.id}
                      className="p-4 rounded-xl bg-secondary/20 border border-white/5 hover:border-primary/20 transition-all duration-200 cursor-pointer group hover:-translate-y-0.5"
                      onClick={() => navigate(`/generate/${gen.video_id}/${gen.preset_id}`)}
                      data-testid={`library-gen-${gen.id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        <Video className="w-3 h-3 text-muted-foreground/60" strokeWidth={1.5} />
                        <span className="text-[10px] text-muted-foreground/60 truncate">{gen.video_filename}</span>
                        <ChevronRight className="w-2.5 h-2.5 text-muted-foreground/40" />
                        <span className="text-[10px] text-primary/60 truncate">{gen.preset_name}</span>
                      </div>
                      <p className="font-heading font-semibold text-sm line-clamp-1 group-hover:text-primary transition-colors">{gen.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{gen.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex gap-1 flex-wrap">
                          {gen.hashtags?.slice(0, 3).map((h, i) => (
                            <Badge key={i} variant="secondary" className="text-[9px] bg-primary/10 text-primary border-0">
                              #{h.replace('#', '')}
                            </Badge>
                          ))}
                        </div>
                        <ArrowRight className="w-3 h-3 text-muted-foreground/30 group-hover:text-primary transition-colors" />
                      </div>
                      <p className="text-[9px] text-muted-foreground/40 mt-2">{formatDate(gen.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                /* List view */
                <div className="space-y-1.5">
                  {filteredGenerations.map((gen) => (
                    <div
                      key={gen.id}
                      className="flex items-center gap-4 p-3 rounded-xl bg-secondary/20 border border-white/5 hover:border-primary/20 transition-all duration-200 cursor-pointer group"
                      onClick={() => navigate(`/generate/${gen.video_id}/${gen.preset_id}`)}
                      data-testid={`library-gen-list-${gen.id}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-sm truncate group-hover:text-primary transition-colors">{gen.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground/60 truncate">{gen.video_filename}</span>
                          <span className="text-[10px] text-muted-foreground/30">·</span>
                          <span className="text-[10px] text-primary/60 truncate">{gen.preset_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {gen.hashtags?.slice(0, 2).map((h, i) => (
                          <Badge key={i} variant="secondary" className="text-[9px] bg-primary/10 text-primary border-0 hidden lg:inline-flex">
                            #{h.replace('#', '')}
                          </Badge>
                        ))}
                        <span className="text-[10px] text-muted-foreground/40 w-16 text-right">{formatDate(gen.created_at).split(',')[0]}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ─── Folder Create/Edit Modal ─── */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent className="bg-zinc-900 border-border/50 max-w-sm" data-testid="folder-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {editingFolder ? 'Edit Folder' : 'Create Folder'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="e.g. Golf Content, Q1 Clips..."
                className="bg-secondary/50 border-transparent focus:border-primary h-10"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSaveFolder()}
                data-testid="folder-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Color</label>
              <div className="flex gap-2">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setFolderColor(c.id)}
                    className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                      c.bg,
                      folderColor === c.id ? 'ring-2 ring-white/30 scale-110' : 'opacity-60 hover:opacity-100'
                    )}
                    data-testid={`color-${c.id}`}
                  >
                    <div className={`w-3 h-3 rounded-full ${c.dot}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFolderModal(false)}>Cancel</Button>
            <Button
              onClick={handleSaveFolder}
              disabled={savingFolder || !folderName.trim()}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="save-folder-button"
            >
              {savingFolder ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editingFolder ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Generate Modal */}
      <BatchGenerateModal
        open={showBatchModal}
        onOpenChange={setShowBatchModal}
        selectedVideoIds={selectedIds.filter(id => videos.find(v => v.id === id)?.status === 'ready')}
        onComplete={handleBatchComplete}
      />
    </div>
  );
}
